import { CustomerContacts } from "./customer";

export enum ORDER_STATE {
  AWAITING_DATE = -1, // Awaiting confirmation of date and time
  BOOKING_IN_PROGRESS = 0,
  BOOKING_PLACED = 1,
  COMPLETE = 2, // Work completed
  CONTENT_SELECTED = 3,
  AWAITING_PAYMENT = 4,
  ORDER_PAID = 5,
  RETURN_TO_WORK = 6,
  AWAITING_CARD_PAYMENT = 7,
  DRAFT = 99,
  CANCELLED = 999,
};

export enum ORDERSERVICE_TYPE {
  CUSTOM= 0,
  PHOTO= 1,
  FLOORPLAN= 2,
  VIRTUAL_TOUR= 3,
  VIDEO4K= 4,
  EPC2= 5,
  SLIDESHOW_VIDEO= 6,
  INTERACTIVE_FLOORPLAN= 7,
  PHOTO_EDIT= 8,
  FLOORPLAN_EDIT= 9,
  VIRTUAL_STAGING= 10,
  VIDEO= 11,
  IMAGEMOTION_VIDEO= 12,
  MATTERPORT= 13,
  WALKLY=14
}

export interface Service {
  id: string;
  image: string;
  name: string;
  price: number;
}

export interface Order {
  badge: string;
  companyName: string;
  dateTaken: string;
  location: string;
  logoUrl?: string;
  id: string;
  price: number;
  services: Service[];
  state: ORDER_STATE;
  status: number;
  title: string;
  workdate: string;
}

export type CustomOrder = Pick<Order, 'id' | 'services' | 'title'>;

export enum CONTACT_TYPE {
  VENDOR,
  TENANT,
  AGENT,
}

export interface Contact {
  contactId: string | null;
  customerId: string;
  email: string;
  name: string;
  phone1: string;
  phone2: string | null;
  sendInvoice: boolean;
  type?: CONTACT_TYPE;
}

export interface Notification {
  createdDateTime: string;
  message: string;
  notificationId: string;
  status: number;
  type: number;
  userName: string;
}

export type OrderNewContact = Omit<CustomerContacts, 'contactId' | 'customerId'>;

export interface OrderContact extends Contact {
  amendedDateTime: string;
  createdDateTime: string;
  email: string;
  name: string;
  id: string;
  orderId: number;
  phone1: string;
  phone2: string | null;
  type: CONTACT_TYPE;
}

export interface OrderOfferedService {
  amendedDateTime: string;
  createdDateTime: string;
  files: number;
  imageURL: string;
  name: string;
  offeredServiceId: string;
  price: number;
  type: number;
  uploadFiles: OrderFile[];
}

export interface PropertyAddress {
  addressId: number;
  addressString: string | null;
  amendedDateTime: string;
  area: string | null;
  country: string | null;
  createdDateTime: string;
  doorNumber: string | null;
  flat: string | null;
  houseName: string | null;
  houseNumber: string | null;
  postcode: string | null;
  road: string | null;
  town: string | null;
}

export interface OrderHdrFile {
  fullURL: string;
  hdrSourceFileId: string;
  thumbURL: string;
}

export interface OrderFile {
  filePaymentStatus: number;
  fileStatus: number;
  fullURL: string;
  hasAnnotation: boolean;
  isUpsell: boolean;
  thumbURL: string;
  uploadFileId: string;
  upsellPrice: number;
}

export interface OrderService {
  amendedDateTime: string;
  amount: number;
  applicationUserId: string;
  applicationUserName: string;
  appointmentDate: string;
  appointmentDuration: number;
  complete: boolean;
  createdDateTime: string;
  description: string;
  draftFiles: number;
  draftUploadFiles: OrderFile[];
  externalLink: string | null;
  files: number;
  floorArea: number;
  hdrSourceFiles: OrderHdrFile[];
  imageURL: string;
  isExtraService: boolean;
  label: string | null;
  name: string;
  notificationMessage: string | null;
  notifications: Notification[];
  orderServiceId: string;
  price: number;
  rejectReason: string | null;
  rejected: boolean;
  serviceAdditionalFields: string | null;
  showConfirm: boolean;
  showSendMessage: boolean;
  sourceFiles: OrderFile[];
  type: ORDERSERVICE_TYPE;
  uploadFiles: OrderFile[];
  virtualTourPhotos: number;
}

export interface OrderNote {
  created?: string;
  id: string;
  content: string;
  owner?: string;
}

export interface OrderDetails {
  accountDetails: {
    accountPassword?: string | null;
    accountPhoneNumber: string;
    active: boolean;
    accountsEmailAddress: string;
    daysToPayInvoices: number;
    firstName: string;
    lastName: string;
    name: string;
    sendEmailNotification?: boolean | null;
  }
  bedrooms: number;
  branchId: number;
  common: {
    appointmentDateTime: string | null;
    appointmentDuration: number | null;
    contactVendorDirectly: boolean;
    notifyViaSMS: boolean;
    sendNotifications: boolean;
  }
  companyName: string;
  contacts: OrderContact[]
  createdDateTime: string;
  customerId: string | null;
  customerUserId: string;
  defaultAmount: number;
  description: string;
  floorArea: number;
  hasInvoice: boolean;
  hasRejections: boolean;
  id: string;
  imageMotionRequested: boolean;
  invoiceLink: string;
  invoiceOverride: boolean;
  isVirtual: boolean;
  keys: boolean;
  keysTime: string;
  keysTimeOption: string;
  keysWith: string;
  keysWithDetail: string;
  maintainer: string | null;
  notes: string;
  offeredServices: OrderOfferedService[];
  orderExtraCharge: {
    orderExtraChargeDescription: string,
    orderExtraChargeId: string,
    orderExtraChargePriceExVat: number,
  }
  orderNotes: OrderNote[];
  orderOfferPrice: number;
  orderPrice: number;
  orderState: number;
  orderTemplateId: number;
  parking: string;
  photoCount: number;
  poNumber: string;
  propertyAddress: PropertyAddress;
  services: OrderService[];
  sizeProperty: number;
  trustedCustomer: boolean;
  upsellPrice: number;
  videoMotionRequested: boolean;
  virtualStagingRequested: boolean;
  virtualTourPartial: boolean;
}

export interface Maintainer {
  email: string;
  firstName: string;
  id: string;
  lastName: string;
}

export interface GroupedOrderServices {
  photoService?: OrderService;
  floorPlanService?: OrderService;
  virtualTourService?: OrderService;
  virtualStagingService?: OrderService;
  imagemotionVideoService?: OrderService;
  walklyService?: OrderService;
  remainingServices: OrderService[];
}